import React from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";

export default function Pricing({ plans }) {
  const navigate = useNavigate();

  const getSavingsLabel = (savings) =>
    Number.isFinite(Number(savings)) ? `Save ${savings}%` : savings || "Included";
  const getPromotionMonths = (promotion) => {
    const months = parseInt(promotion || 0);
    return Number.isFinite(months) ? months : 0;
  };

  const handleSubscriptionSelection = (selectedPlan) => {
    navigate("/checkout", {
      state: { subscriptionData: { ...selectedPlan } },
    });
  };

  return (
    <section className="bg-white px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
      <div className="mx-auto max-w-7xl rounded-lg bg-slate-50 px-4 py-12 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-sm font-bold uppercase tracking-[0.18em] text-gray-500">
            Pricing
          </p>
          <h2 className="mt-3 text-3xl font-black tracking-tight text-gray-950 sm:text-4xl">
            Choose the plan that fits your PTE timeline.
          </h2>
          <p className="mt-4 text-base leading-7 text-gray-600">
            Start with the access window you need. Every plan includes AI
            practice, mock tests, and performance feedback.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3 lg:gap-8">
        {plans.length > 0 ? (
          plans.map((plan, index) => (
            <div
              key={index}
              className={`relative flex w-full max-w-sm flex-col rounded-lg border p-6 shadow-sm transition hover:-translate-y-1 hover:shadow-xl hover:shadow-gray-900/10 ${
                plan.plan === "Premium"
                  ? "border-fuchsia-900 bg-white text-gray-900 md:scale-[1.03]"
                  : "border-gray-200 bg-white text-gray-900"
              } mx-auto`}
            >
              <div className="mb-5 flex items-center justify-between">
                <p
                  className={`rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide ${
                    plan.plan === "Premium"
                      ? "bg-fuchsia-900 text-white"
                      : "bg-gray-100 text-gray-700"
                  }`}
                >
                  {plan.plan === "Premium" ? "Most popular" : "Popular"}
                </p>
                <p className="rounded-full bg-fuchsia-50 px-3 py-1 text-xs font-bold text-fuchsia-800">
                  {getSavingsLabel(plan.savings)}
                </p>
              </div>

              <div className="mb-5">
                <h3 className="text-xl font-bold text-gray-950">{plan.plan}</h3>
                <div className="mt-2 flex items-center gap-2">
                  <p className="text-sm font-semibold text-gray-700">
                      {getPromotionMonths(plan.promotion) > 0
                        ? `${
                            plan.duration - getPromotionMonths(plan.promotion)
                          } Months`
                        : `${plan.duration} Months`}
                  </p>
                  {getPromotionMonths(plan.promotion) > 0 && (
                    <p className="text-sm font-bold text-fuchsia-700">
                        + {plan.promotion}
                    </p>
                  )}
                </div>
              </div>

              <div className="border-y border-gray-100 py-5">
                <div className="flex items-baseline">
                  <span className="text-4xl font-black text-gray-950">
                    ${plan.price}
                  </span>
                  <span className="ml-1 text-sm text-gray-500">/mo</span>
                  <p className="ml-3 text-lg text-gray-500">
                    <span className="line-through text-gray-400">
                      {plan.cancelledMRP}
                    </span>
                  </p>
                </div>
                <p
                  className={`mt-3 text-sm leading-6 ${
                    plan.plan === "Premium" ? "text-gray-700" : "text-gray-500"
                  }`}
                >
                  {plan.additionalDetails}
                </p>
              </div>

              <ul className="mt-6 flex-1 space-y-3">
                {plan.features.map((feature, featureIndex) => (
                  <li
                    key={featureIndex}
                    className="flex items-start gap-3 text-sm leading-6 text-gray-700"
                  >
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-indigo-600" />
                    {feature}
                  </li>
                ))}
              </ul>
              <button
                className={`mt-8 block w-full rounded-md px-6 py-3 text-center text-sm font-semibold transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2 ${
                  plan.plan === "Premium"
                    ? "bg-gradient-to-r from-fuchsia-900 to-fuchsia-700 text-white shadow-lg shadow-fuchsia-900/15 hover:-translate-y-0.5 hover:opacity-90"
                    : "border border-fuchsia-900 bg-white text-fuchsia-800 hover:bg-gradient-to-r hover:from-fuchsia-900 hover:to-fuchsia-700 hover:text-white"
                }`}
                onClick={() => handleSubscriptionSelection(plan)}
              >
                {plan.plan === "Premium"
                  ? "Get Special Deal"
                  : `${plan.duration} Month Subscription`}
              </button>
              <p className="mt-3 text-center text-xs text-gray-500">
                Powered by PrepNexis AI
              </p>
            </div>
          ))
        ) : (
          <div className="col-span-full rounded-lg border border-dashed border-gray-300 bg-white p-8 text-center text-gray-600">
            Plans are loading. Please check back in a moment.
          </div>
        )}
        </div>
      </div>
    </section>
  );
}
